import React from "react";
import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay, isYesterday, isSameMonth, isSameYear } from "date-fns";
import { es } from "date-fns/locale";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Typography from "@material-ui/core/Typography";
import Avatar from "@material-ui/core/Avatar";
import Divider from "@material-ui/core/Divider";
import GroupIcon from "@material-ui/icons/Group";
import PersonIcon from "@material-ui/icons/Person";

import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  ticket: {
    position: "relative",
  },
  contactNameWrapper: {
    display: "flex",
    justifyContent: "space-between",
  },
  lastMessageTime: {
    justifySelf: "flex-end",
    minWidth: 80,
    textAlign: "right",
  },
  contactLastMessage: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
    flex: 1,
    display: "block",
  },
  messagePreviewRow: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    width: "100%",
    paddingRight: 8,
  },
  listItemTextSecondary: {
    minWidth: 0,
    overflow: "hidden",
    width: "100%",
  },
}));

const InternalChatListItem = ({ chat, user }) => {
  const classes = useStyles();
  const history = useHistory();
  const { chatId } = useParams();

  const formatChatTime = (dateString) => {
    if (!dateString) return "";
    const date = parseISO(dateString);
    const now = new Date();
    if (isSameDay(date, now)) {
      const timeStr = format(date, "h:mm a", { locale: es });
      return timeStr.replace(/\s(am|pm)/i, (match, period) => {
        return ` ${period.toLowerCase() === "am" ? "a.m." : "p.m."}`;
      });
    }
    if (isYesterday(date)) return "ayer";
    if (isSameMonth(date, now)) return format(date, "EEE d", { locale: es });
    if (isSameYear(date, now)) return format(date, "MMMM d", { locale: es });
    return format(date, "MMMM d, yyyy", { locale: es });
  };

  const getDisplayName = () => {
    if (chat.isGroup && chat.name) return chat.name;
    if (chat.users && chat.users.length > 0) {
      const other = chat.users.find((u) => u.id !== user?.id) || chat.users[0];
      return other.name;
    }
    return i18n.t("internalChat.header.conversation");
  };

  const getAvatar = () => {
    if (chat.isGroup) {
      return (
        <Avatar style={{ backgroundColor: "#075E54" }}>
          <GroupIcon />
        </Avatar>
      );
    }
    if (chat.users && chat.users.length > 0) {
      const other = chat.users.find((u) => u.id !== user?.id) || chat.users[0];
      return (
        <Avatar style={{ backgroundColor: "#075E54" }}>
          {other.name ? other.name.charAt(0).toUpperCase() : <PersonIcon />}
        </Avatar>
      );
    }
    return (
      <Avatar style={{ backgroundColor: "#075E54" }}>
        <PersonIcon />
      </Avatar>
    );
  };

  const handleSelectChat = (id) => {
    history.push(`/internal-chats/${id}`);
  };

  return (
    <React.Fragment key={chat.id}>
      <ListItem
        dense
        button
        onClick={() => handleSelectChat(chat.id)}
        selected={chatId && +chatId === chat.id}
        className={classes.ticket}
      >
        <ListItemAvatar>{getAvatar()}</ListItemAvatar>
        <ListItemText
          disableTypography
          classes={{ secondary: classes.listItemTextSecondary }}
          primary={
            <span className={classes.contactNameWrapper}>
              <Typography
                noWrap
                component="span"
                variant="body2"
                color="textPrimary"
                style={{ flex: 1, minWidth: 0 }}
              >
                {getDisplayName()}
              </Typography>
              <Typography
                className={classes.lastMessageTime}
                component="span"
                variant="body2"
                color="textSecondary"
              >
                {formatChatTime(chat.updatedAt)}
              </Typography>
            </span>
          }
          secondary={
            <span className={classes.messagePreviewRow}>
              <Typography
                className={classes.contactLastMessage}
                noWrap
                component="span"
                variant="body2"
                color="textSecondary"
              >
                {chat.lastMessage || ""}
              </Typography>
            </span>
          }
        />
      </ListItem>
      <Divider variant="inset" component="li" />
    </React.Fragment>
  );
};

export default InternalChatListItem;
